/**
 * Root React Router for workflow-directory (internal team app + public job board).
 * - Public: job board, apply, legacy per-client dashboards, workflows tools.
 * - Protected /app/*: recruiters, AMs, admins (see AppLayout + ProtectedRoute).
 * - AuthTokenRedirect: forwards magic-link token_hash to candidate portal when needed.
 * Supabase: browser uses anon client; privileged work lives in api/* (Vercel).
 */
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { Toaster } from "react-hot-toast";
import ProtectedRoute from './Components/ProtectedRoute.jsx';
import RoleGate from './Components/RoleGate.jsx';
import ErrorBoundary from './Components/ErrorBoundary.jsx';
import LoadingScreen from './Components/LoadingScreen.jsx';
import ContentProducerRoleJuanFelipe from './Pages/CPJuanFelipe.jsx';
import ContentProducerRoleJuanEsteban from './Pages/CPJuanEsteban.jsx';
import ContentProducerRoleLinhue from './Pages/CPLinhue.jsx';
import CandidateFitDashboardContentProducerManny from './Pages/MannyTCetralCP.jsx';
import SocialMediaCreativeStrategistKeisy from './Pages/CSKEISY.jsx';
import SocialMediaCreativeStrategistDaniela from './Pages/CSDANIELAURIBE.jsx';
import SocialMediaCreativeStrategistSantiago from './Pages/CSSantiago.jsx';
import SocialMediaCreativeStrategistTomas from './Pages/CSTomas.jsx';
import CandidateFitDashboardChad from './Pages/ChadTCentral.jsx';
import HeadOfPartnershipsMichelle from './Pages/HP2MichelleT.jsx';
import HeadOfPartnershipsKimberly from './Pages/HP2KimPotter.jsx';
import HeadOfPartnershipsLouis from './Pages/HP2LouisK.jsx';
import HeadOfPartnershipsDawn from './Pages/HP2DawnB.jsx';
import HeadOfPartnershipsJonathan from './Pages/HP2JonathanM.jsx';
import HeadOfPartnershipsDashboard from './Pages/HP2Dashboard.jsx';



const WorkflowDirectory = lazy(() => import('./Pages/WorkflowDirectory.js'));
const AIReportForm = lazy(() => import('./Pages/AIReportForm.js'));
const AIReportResults = lazy(() => import('./Pages/AIReportResults'));
const AIRoiReportBuilder = lazy(() => import('./Pages/AIRoiReportBuilder.js'));
const AIWorkflowsTable = lazy(() => import('./Pages/aia-workflows-table.js'));
const PublicJobBoard = lazy(() => import('./Pages/PublicJobBoard.jsx'));
const JobBoard = lazy(() => import('./Pages/JobBoard.jsx'));
const JobBoardLight = lazy(() => import('./Pages/JobBoardLight.jsx'));
const Login = lazy(() => import('./Pages/Login.js'));
const ThuyRoleCristina = lazy(() => import("./Pages/ThuyRoleCristina.jsx"));
const ThuyRoleValentina = lazy(() => import("./Pages/ThuyRoleValentina.jsx"));
const ThuyRoleMarianela = lazy(() => import("./Pages/ThuyRoleMarianela.jsx"));
const ThuyRoleJohan = lazy(() => import("./Pages/ThuyRoleJohan.jsx"));
const ThuyRoleValeria = lazy(() => import("./Pages/ThuyRoleValeria.jsx"));
const SDRRoleMariana = lazy(() => import('./Pages/SDRRoleMariana.jsx'));
const SDRRoleLuis = lazy(() => import('./Pages/SDRRoleLuis.jsx'));
const SDRRoleGabriel = lazy(() => import('./Pages/SDRGabriel.jsx'));
const SDRRoleFrancisco = lazy(() => import('./Pages/SDRRFrancisco.jsx'));
const SDRRoleYanet = lazy(() => import('./Pages/SDRRYanet.jsx'));
const SDRRoleValentinaMateluna = lazy(() => import('./Pages/SDRRValentina.jsx'));
const SDRRoleJulio = lazy(() => import('./Pages/SDRRJulio.jsx'));
const SDRRoleDashboard = lazy(() => import('./Pages/SDRRDashboard.jsx'));
const DarusCandidates = lazy(() => import('./Pages/DarusTCentral.jsx'));
const SalesProfiles = lazy(() => import('./Pages/SalesProfiles.jsx'));
const DRMRyanChristmas = lazy(() => import('./Pages/DRMRoleRyan.jsx'));
const DRMAndresVictoria = lazy(() => import('./Pages/DRMAndres.jsx'));
const DRMJonathan = lazy(() => import('./Pages/DRMJonathan.jsx'));
const CarlosCandidateFitDashboard = lazy(() => import('./Pages/CarlosTCentral.jsx'));
const CandidateFitDashboardJohn = lazy(() => import('./Pages/JohnTCentral.jsx'));
const CandidateFitDashboardJeff = lazy(() => import('./Pages/JeffTCentral.jsx'));
const CandidateFitDashboardAustin = lazy(() => import('./Pages/AustinTCentral.jsx'));
const ContentProducerRoleMaria = lazy(() => import('./Pages/CPMaria.jsx'));
const ContentProducerRoleDaniel = lazy(() => import('./Pages/CPDaniel.jsx'));
const ContentProducerRoleJuan = lazy(() => import('./Pages/CPJuan.jsx'));
const CandidateFitDashboardKevin = lazy(() => import('./Pages/KevinTCentral.jsx'));
const CandidateFitDashboardManny = lazy(() => import('./Pages/MannyTCentral.jsx'));
const CommandCenterDraft = lazy(() => import('./Pages/DraftCommandCenter.jsx'));
const AMDashboardDraft = lazy(() => import('./Pages/DraftAMDashboard.jsx'));
const ClientPortalDraft = lazy(() => import('./Pages/DraftClientPortal.jsx'));
const ClientProfilePage = lazy(() => import("./Pages/ClientProfilePage"));
const ClientCandidateDashboardPage = lazy(() => import("./Pages/ClientCandidateDashboardPage"));
const WeeklyReportDashboardExample = lazy(() => import('./Pages/ClientReportDashboard.jsx'));

const AppLayout = lazy(() => import('./layouts/AppLayout.jsx'));
const AdminDashboard = lazy(() => import('./Pages/AdminDashboard.jsx'));
const AdminAnalytics = lazy(() => import('./Pages/AdminAnalytics.jsx')); // kept at /app/analytics/legacy

// ── Analytics dashboard suite (/app/analytics/*) ──────────────────────────
const AnalyticsLayout = lazy(() => import('./Pages/analytics/AnalyticsLayout'));
const SalesIntelligenceLayout = lazy(() => import('./Pages/sales-intelligence/SalesIntelligenceLayout'));
const LeadsManagerPage = lazy(() => import('./Pages/sales-intelligence/LeadsManagerPage'));
const SalesCalendarPage = lazy(() => import('./Pages/sales-intelligence/CalendarPage'));
const LeadDetailPage = lazy(() => import('./Pages/sales-intelligence/LeadDetailPage'));
const SalesPreCallReportPage = lazy(() => import('./Pages/sales-intelligence/SalesPreCallReportPage'));
const SalesRoiReportPage = lazy(() => import('./Pages/sales-intelligence/SalesRoiReportPage'));
const SalesSettingReportPage = lazy(() => import('./Pages/sales-intelligence/SalesSettingReportPage'));
const SalesReportPage = lazy(() => import('./Pages/sales-intelligence/SalesReportPage'));
const AnalyticsGalleryPage = lazy(() => import('./Pages/analytics/GalleryPage'));
const AnalyticsOverviewPage = lazy(() => import('./Pages/analytics/OverviewPage'));
const AnalyticsPipelinePage = lazy(() => import('./Pages/analytics/PipelinePage'));
const AnalyticsDataLogPage = lazy(() => import('./Pages/analytics/DataLogPage'));
const AnalyticsProspectDetailPage = lazy(() => import('./Pages/analytics/ProspectDetailPage'));
const AnalyticsMarketingSalesPage = lazy(() => import('./Pages/analytics/MarketingSalesPage'));
const AnalyticsSalesPage = lazy(() => import('./Pages/analytics/SalesPage'));
const AnalyticsCandidateAcquisitionPage = lazy(() => import('./Pages/analytics/CandidateAcquisitionPage'));
const AdminApplications = lazy(() => import('./Pages/AdminApplications.jsx'));
const AdminCandidates = lazy(() => import("./Pages/AdminCandidates.jsx"));
const ProfilePage = lazy(() => import("./Pages/ProfilePage.jsx"));
const AdminClients = lazy(() => import("./Pages/AdminClients"));
const ClientDetail = lazy(() => import("./Pages/ClientDetail"));
const AMActionItems = lazy(() => import('./Pages/am/ActionItems.jsx'));
const AMReporting = lazy(() => import('./Pages/am/Reporting.jsx'));
const AMPortfolio = lazy(() => import('./Pages/am/Portfolio.jsx'));
const AMInbox = lazy(() => import('./Pages/Inbox.jsx'));
const RecruiterMyHirings = lazy(() => import('./Pages/recruiter/MyHirings.jsx'));
const AdminUsers = lazy(() => import('./Pages/admin/Users.jsx'));
const ArchivedUsers = lazy(() => import('./Pages/admin/ArchivedUsers.jsx'));
const AdminFeedback = lazy(() => import('./Pages/admin/Feedback.jsx'));
const AdminNotificationControl = lazy(() => import('./Pages/admin/NotificationControl.jsx'));
const ApiAccess = lazy(() => import('./Pages/ApiAccess.jsx'));
const AdminFulfillmentDashboard = lazy(() => import('./Pages/AdminFulfillmentDashboard.jsx'));
const AdminOpenClaw = lazy(() => import('./Pages/AdminOpenClaw.jsx'));
const AdminRiskTracker = lazy(() => import('./Pages/AdminRiskTracker.jsx'));
const AuthCallbackPage = lazy(() => import('./Pages/auth/AuthCallbackPage.jsx'));
const SetPasswordPage = lazy(() => import('./Pages/auth/SetPasswordPage.jsx'));

const PORTAL_URL =
  process.env.REACT_APP_CANDIDATE_PORTAL_URL || "https://portal.aiarchitech.com";

function JobRedirect() {
  const { shortCode } = useParams();
  return <Navigate to={`/?job=${shortCode}`} replace />;
}

function AdminProfileRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/candidates/${id}`} replace />;
}

function AdminAnalyticsPrefixRedirect() {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.replace(/^\/app\/admin\/analytics/, "") || "";
  const to = `/app/analytics${rest}${search}${hash}`;
  return <Navigate to={to} replace />;
}

function AuthTokenRedirect() {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tokenHash = searchParams.get("token_hash");
    if (!tokenHash) return;

    const type = searchParams.get("type");

    // Invite and recovery links for the admin app stay here
    if (type === "invite" || type === "recovery") {
      const adminCallback = new URL("/auth/callback", window.location.origin);
      adminCallback.searchParams.set("token_hash", tokenHash);
      if (type) adminCallback.searchParams.set("type", type);
      window.location.replace(adminCallback.toString());
      return;
    }

    // Everything else (magic link etc.) forwards to the candidate portal
    const portalCallback = new URL("/auth/callback", PORTAL_URL);
    portalCallback.searchParams.set("token_hash", tokenHash);
    if (type) portalCallback.searchParams.set("type", type);
    const ref = searchParams.get("ref");
    if (ref) portalCallback.searchParams.set("ref", ref);
    window.location.replace(portalCallback.toString());
  }, [searchParams]);
  return null;
}

export default function App() {
  return (
    <Router>
      <AuthTokenRedirect />
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen message="Loading page..." variant="light" />}>
          <Routes>
            {/* --- Route groups: public → protected /app → legacy /admin redirects --- */}

            {/* ═══ Public Routes ═══ */}
            <Route path="/" element={<PublicJobBoard />} />
            <Route path="/join" element={<JobBoard />} />
            <Route path="/j/:shortCode" element={<JobRedirect />} />
            <Route path="/v2" element={<JobBoardLight />} />
            <Route path="/workflows" element={<WorkflowDirectory />} />
            <Route path="/AI-report" element={<AIReportForm />} />
            <Route path="/AI-report-results/:id" element={<AIReportResults />} />
            <Route path="/AI-roi-report" element={<AIRoiReportBuilder />} />
            <Route path="/ai/:code" element={<SalesReportPage />} />
            <Route path="/sales/report/:token" element={<SalesPreCallReportPage />} />
            <Route path="/sales/roi/:token" element={<SalesRoiReportPage />} />
            <Route path="/sales/setting/:token" element={<SalesSettingReportPage />} />
            <Route path="/aia-workflows-table" element={<AIWorkflowsTable />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/set-password" element={<SetPasswordPage />} />
            <Route path="/client/profile/:applicationId" element={<ClientProfilePage />} />
            <Route path="/client/dashboard/:dashboardId" element={<ClientCandidateDashboardPage />} />
            <Route path="/client/candidates/cristina" element={<ThuyRoleCristina />} />
            <Route path="/client/candidates/valentina" element={<ThuyRoleValentina />} />
            <Route path="/client/candidates/marianela" element={<ThuyRoleMarianela />} />
            <Route path="/client/candidates/johan" element={<ThuyRoleJohan />} />
            <Route path="/client/candidates/valeria" element={<ThuyRoleValeria />} />
            <Route path="/client/candidates/mariana" element={<SDRRoleMariana />} />
            <Route path="/client/candidates/luis" element={<SDRRoleLuis />} />
            <Route path="/client/candidates/gabriel" element={<SDRRoleGabriel />} />
            <Route path="/client/candidates/francisco" element={<SDRRoleFrancisco />} />
            <Route path="/client/candidates/yanet" element={<SDRRoleYanet />} />
            <Route path="/client/candidates/valentinaMateluna" element={<SDRRoleValentinaMateluna />} />
            <Route path="/client/candidates/julio" element={<SDRRoleJulio />} />
            <Route path="/client/SDRDashboard" element={<SDRRoleDashboard />} />
            <Route path="/darus/candidates" element={<DarusCandidates />} />
            <Route path="/candidatefitdashboard" element={<SalesProfiles />} />
            <Route path="/timereportdashboard" element={<WeeklyReportDashboardExample />} />
            <Route path="/client/candidates/ryan" element={<DRMRyanChristmas />} />
            <Route path="/client/candidates/andres" element={<DRMAndresVictoria />} />
            <Route path="/client/candidates/jonathan" element={<DRMJonathan />} />
            <Route path="/client/candidates/juanFelipe" element={<ContentProducerRoleJuanFelipe />} />
            <Route path="/client/candidates/juanEsteban" element={<ContentProducerRoleJuanEsteban />} />
            <Route path="/client/candidates/MichelleT" element={<HeadOfPartnershipsMichelle />} />
            <Route path="/client/candidates/KimberlyP" element={<HeadOfPartnershipsKimberly />} />
            <Route path="/client/candidates/LouisK" element={<HeadOfPartnershipsLouis />} />
            <Route path="/client/candidates/DawnB" element={<HeadOfPartnershipsDawn />} />
            <Route path="/client/candidates/JonathanM" element={<HeadOfPartnershipsJonathan />} />
<Route path="/client/HPDashboard" element={<HeadOfPartnershipsDashboard />} />


            <Route path="/client/candidates/Linhue" element={<ContentProducerRoleLinhue />} />
            <Route path="/client/candidates/Keisy" element={<SocialMediaCreativeStrategistKeisy />} />
            <Route path="/client/candidates/DanielaU" element={<SocialMediaCreativeStrategistDaniela />} />
            <Route path="/client/candidates/Santiago" element={<SocialMediaCreativeStrategistSantiago />} />
            <Route path="/client/candidates/Tomas" element={<SocialMediaCreativeStrategistTomas />} />
            <Route path="/manny/content" element={<CandidateFitDashboardContentProducerManny />} />
            <Route path="/chad/content-strategist" element={<CandidateFitDashboardChad />} />
            <Route path="/carlos/candidates" element={<CarlosCandidateFitDashboard />} />
            <Route path="/john/candidates" element={<CandidateFitDashboardJohn />} />
            <Route path="/jeffery/candidates" element={<CandidateFitDashboardJeff />} />
            <Route path="/austin/candidates" element={<CandidateFitDashboardAustin />} />
            <Route path="/client/candidates/maria" element={<ContentProducerRoleMaria />} />
            <Route path="/client/candidates/daniel" element={<ContentProducerRoleDaniel />} />
            <Route path="/client/candidates/juan" element={<ContentProducerRoleJuan />} />
            <Route path="/kevin/candidates" element={<CandidateFitDashboardKevin />} />
            <Route path="/manny/candidates" element={<CandidateFitDashboardManny />} />

            {/* Draft pages (local preview only) */}
            <Route path="/draft/command-center" element={<CommandCenterDraft />} />
            <Route path="/draft/am-dashboard" element={<AMDashboardDraft />} />
            <Route path="/draft/client-portal" element={<ClientPortalDraft />} />

            {/* ═══ Protected /app/* routes with sidebar layout ═══ */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                {/* Recruitment */}
                <Route path="/app/job-board" element={<AdminDashboard />} />
                <Route path="/app/candidates" element={<AdminCandidates />} />
                <Route path="/app/candidates/:id" element={<ProfilePage />} />
                <Route path="/app/clients" element={<AdminClients />} />
                <Route path="/app/clients/:id" element={<ClientDetail />} />
                <Route path="/app/fulfillment" element={<AdminFulfillmentDashboard />} />
                <Route path="/app/applications" element={<AdminApplications />} />

                {/* Business Analytics nav used /app/admin/analytics — routes live under /app/analytics; redirect old URLs */}
                <Route path="/app/admin/analytics/*" element={<AdminAnalyticsPrefixRedirect />} />

                {/* Analytics dashboard suite (/app/analytics/*) */}
                <Route path="/app/analytics" element={<AnalyticsLayout />}>
                  {/* Gallery = landing page */}
                  <Route index element={<AnalyticsGalleryPage />} />
                  {/* Individual dashboards */}
                  <Route path="overview" element={<AnalyticsOverviewPage />} />
                  <Route path="marketing-sales" element={<AnalyticsMarketingSalesPage />} />
                  <Route path="sales" element={<AnalyticsSalesPage />} />
                  <Route path="candidate-acquisition" element={<AnalyticsCandidateAcquisitionPage />} />
                  <Route path="pipeline" element={<AnalyticsPipelinePage />} />
                  <Route path="data-log" element={<AnalyticsDataLogPage />} />
                  <Route path="prospects/:id" element={<AnalyticsProspectDetailPage />} />
                  {/* Legacy analytics page preserved during migration */}
                  <Route path="legacy" element={<AdminAnalytics />} />
                </Route>

                {/* Sales Intelligence (sales + admin) */}
                <Route
                  path="/app/sales-intelligence"
                  element={
                    <RoleGate allowedRoles={["admin", "sales"]} redirectTo="/">
                      <SalesIntelligenceLayout />
                    </RoleGate>
                  }
                >
                  <Route index element={<Navigate to="leads" replace />} />
                  <Route path="leads" element={<LeadsManagerPage />} />
                  <Route path="leads/:leadId" element={<LeadDetailPage />} />
                  <Route path="calendar" element={<SalesCalendarPage />} />
                </Route>

                {/* Operations (Account Manager) */}
                <Route path="/app/action-items" element={<AMActionItems />} />
                <Route path="/app/reporting" element={<AMReporting />} />
                <Route path="/app/portfolio/*" element={<AMPortfolio />} />
                <Route path="/app/inbox" element={<AMInbox />} />

                {/* Recruiter */}
                <Route path="/app/my-hirings" element={<RecruiterMyHirings />} />

                {/* Management */}
                <Route path="/app/users" element={<AdminUsers />} />
                <Route path="/app/users/archived" element={<ArchivedUsers />} />
                <Route path="/app/feedback" element={<AdminFeedback />} />
                <Route path="/app/openclaw" element={<AdminOpenClaw />} />
                <Route path="/app/notifications" element={<AdminNotificationControl />} />
                <Route path="/app/risk-tracker" element={<AdminRiskTracker />} />

                {/* Developer — read-only reporting API access (admin + api_access) */}
                <Route
                  path="/app/api-access"
                  element={
                    <RoleGate allowedRoles={["admin", "api_access"]} redirectTo="/app/inbox">
                      <ApiAccess />
                    </RoleGate>
                  }
                />
              </Route>
            </Route>

            {/* ═══ Backward-compatibility redirects from /admin/* ═══ */}
            <Route path="/admin" element={<Navigate to="/app/job-board" replace />} />
            <Route path="/admin/analytics" element={<Navigate to="/app/analytics" replace />} />
            <Route path="/admin/applications" element={<Navigate to="/app/applications" replace />} />
            <Route path="/admin/candidates" element={<Navigate to="/app/candidates" replace />} />
            <Route path="/admin/profile/:id" element={<AdminProfileRedirect />} />
            <Route path="/admin/clients" element={<Navigate to="/app/clients" replace />} />

          </Routes>
        </Suspense>
      </ErrorBoundary>

      <Toaster position="bottom-right" toastOptions={{ duration: 3000 }} />
    </Router>
  );
}
